import { useMemo } from "react";
import { useGetRecordSpansQuery } from "@/api/api";
import {
  formatFittedRange,
  useAllRecordsTarget,
} from "@/common/allRecordsRange";
import { fitIntervalsToSeconds } from "@/common/fitRange";
import { bucketRecordSpans, RecordSpan } from "@/common/intervalBuckets";
import { QuickIntervalTarget } from "@/common/quickIntervals";
import { safeName } from "@/common/recordTree";
import { HGInterval } from "@/common/types";
import { Separator } from "@/components/ui/separator";

/**
 * `getName` composes its parts around optional pieces, so a name whose era or
 * qualifier slot is empty comes back with a doubled or trailing space —
 * invisible in the picker's centred rows, obvious at the head of a
 * left-aligned one.
 */
function rowLabel(interval: HGInterval): string {
  return safeName(interval).replace(/\s+/g, " ").trim();
}

/**
 * One destination: what it is on the left, how many records are there on the
 * right.
 *
 * The count sits in a column of its own — `ml-auto` against the shared width
 * of the list, `tabular-nums` so the digits line up between rows — because the
 * names it follows run from "1789" to "3 January 1789, Saturday" and a count
 * tucked in after each of those lands nowhere in particular. Same treatment as
 * the timeline's own counts, so the empty state reads as the same surface as
 * the list it stands in for.
 */
function JumpRow({
  label,
  hint,
  count,
  onClick,
}: {
  label: string;
  /** A muted second column, only where the label alone is not specific. */
  hint?: string;
  /** Absent when the number would be a lie — see `total` below. */
  count?: number;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex w-full cursor-pointer items-baseline gap-3 rounded-sm px-2 py-1.5 text-left text-sm hover:bg-row-hover"
    >
      <span className="truncate">{label}</span>
      {hint && <span className="truncate text-xs text-ink2">{hint}</span>}
      {count !== undefined && (
        <span className="ml-auto shrink-0 font-mono text-[10.5px] tabular-nums text-ink2">
          {count.toLocaleString()}
        </span>
      )}
    </button>
  );
}

interface RecordsEmptyProps {
  message: string;
  /**
   * Whether to look for somewhere else to send the user. False whenever the
   * emptiness is not about time — outside a collection, or while a search is
   * filtering the list.
   */
  enabled: boolean;
  userID?: string;
  /** The *resolved* collection id, never the slug the URL carries. */
  collectionID?: string;
  /** The same narrowing the list applies, so the counts describe that list. */
  collectionIDs?: string[];
  onNavigate: (interval: HGInterval, endInterval?: HGInterval | null) => void;
}

/**
 * The records list with nothing in it.
 *
 * On a collection page the message alone is a dead end: the records exist,
 * they are simply somewhere else on the time scale, and the only ways out —
 * "All records", re-clicking the open collection — widen to the whole
 * collection without saying where inside it to look (and re-clicking, once the
 * range already fits, flips to the notes tab instead). So the message is
 * followed by the intervals that do hold records, with their counts, and by a
 * "Show all" row covering the lot — the answer when none of the intervals is
 * the one wanted, and the only offer left when there are no intervals to list
 * (a truncated span list, or no level of the scale that buckets them
 * usefully).
 */
export default function RecordsEmpty({
  message,
  enabled,
  userID,
  collectionID,
  collectionIDs,
  onNavigate,
}: RecordsEmptyProps) {
  const { data } = useGetRecordSpansQuery(
    { userID, collectionID, collectionIDs },
    { skip: !enabled || !userID || !collectionID },
  );

  const buckets = useMemo(() => {
    if (!data || data.truncated) return [];
    const spans: RecordSpan[] = data.spans.map((span) => ({
      start: BigInt(span.start),
      end: BigInt(span.end),
      count: span.count,
    }));
    return bucketRecordSpans(spans);
  }, [data]);

  /**
   * The range every record in scope fits inside, fitted from the very spans
   * the rows above come from.
   *
   * From the spans rather than from `useAllRecordsTarget` because this request
   * carries `collectionIDs` — the same narrowing the list applies for the
   * "direct" and "children" collection scopes — while the time-range endpoint
   * behind that hook always answers for the whole effective subtree. Under
   * "direct" the two disagree, and a row built on the wider of them would
   * widen the range to a span that still shows nothing.
   */
  const spansTarget = useMemo((): QuickIntervalTarget | null => {
    if (!data || data.truncated || data.spans.length === 0) return null;
    let min = BigInt(data.spans[0].start);
    let max = BigInt(data.spans[0].end);
    for (const span of data.spans) {
      const start = BigInt(span.start);
      const end = BigInt(span.end);
      if (start < min) min = start;
      if (end > max) max = end;
    }
    return fitIntervalsToSeconds(min, max);
  }, [data]);

  // The fallback for a truncated span list, whose ends say nothing about the
  // real edges. Already in the cache — the boot fit and the sidebar both
  // subscribe to it — so the wider answer costs nothing, and a scope mismatch
  // is a better offer than the dead end truncation used to produce.
  const { target: subtreeTarget } = useAllRecordsTarget();
  const allTarget = enabled ? (spansTarget ?? subtreeTarget) : null;

  // Only where the span list is whole: a truncated one is a prefix, and its
  // sum would understate the collection by an unknowable amount.
  const total = useMemo(
    () =>
      data && !data.truncated
        ? data.spans.reduce((sum, span) => sum + span.count, 0)
        : undefined,
    [data],
  );

  return (
    <div className="flex grow flex-col items-center justify-center gap-4 px-4">
      <p className="text-sm text-muted-foreground">{message}</p>
      {(buckets.length > 0 || allTarget) && (
        <div className="flex w-full max-w-xs flex-col">
          {buckets.map((bucket) => (
            <JumpRow
              key={`${bucket.interval.it}:${bucket.interval.n}`}
              label={rowLabel(bucket.interval)}
              count={bucket.count}
              onClick={() => onNavigate(bucket.interval)}
            />
          ))}
          {allTarget && (
            <>
              {buckets.length > 0 && <Separator className="my-1" />}
              <JumpRow
                label="Show all"
                hint={formatFittedRange(allTarget)}
                count={total}
                onClick={() =>
                  onNavigate(allTarget.interval, allTarget.endInterval)
                }
              />
            </>
          )}
        </div>
      )}
    </div>
  );
}
